import { useEffect, useRef } from 'react';
import { useTheme } from 'next-themes';
import { useApp } from '@/components/hooks/useApp';
import { safeStorage } from '@/lib/safeStorage';
import { buildThemeCss } from '@/lib/buildThemeCss';
import { DEFAULT_TENANT_THEME } from '@/lib/themeDefaults';

const STORAGE_KEY = 'verix_tenant_theme';
const STYLE_ID = 'tenant-theme';

function applyCss(css) {
  if (typeof document === 'undefined') return;
  let el = document.getElementById(STYLE_ID);
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  if (el.textContent !== css) el.textContent = css;
}

function readCachedTheme() {
  const raw = safeStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    safeStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

/**
 * Wires company.theme → CSS variables on :root via <style id="tenant-theme">.
 *
 * Anti-FOUC: on mount, reads the last-applied theme from localStorage and
 * paints it before the AppProvider has resolved /api/auth/me. When
 * company.theme arrives (or changes via switchCompany), re-paints and
 * persists. Falls back to DEFAULT_TENANT_THEME (Atria/Indigo/cozy) when
 * a tenant has no theme persisted yet.
 */
export function useTenantTheme() {
  const { company, loading } = useApp();
  const { resolvedTheme } = useTheme();
  const mode = resolvedTheme === 'dark' ? 'dark' : 'light';
  const hydratedRef = useRef(false);

  // Anti-FOUC: paint cached theme synchronously on first effect, before
  // company has had a chance to resolve.
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const cached = readCachedTheme();
    if (cached) applyCss(buildThemeCss(cached, mode));
  }, [mode]);

  // Re-paint whenever the active company's theme or the light/dark mode changes.
  // While auth is still loading, leave the cached/default paint in place — we
  // don't want to flash to indigo just because the fetch hasn't returned yet.
  useEffect(() => {
    if (loading) return;
    const theme = company?.theme || DEFAULT_TENANT_THEME;
    const css = buildThemeCss(theme, mode);
    applyCss(css);
    if (company?.theme) {
      try {
        safeStorage.setItem(STORAGE_KEY, JSON.stringify(company.theme));
      } catch {
        /* ignored — quota or serialization */
      }
    }
  }, [company?.theme, mode, loading]);
}
